import { Address as _Address, UpdateAddressContactInput } from '@/gql/graphql';
import AddEditAddressContactsControl from '@/ui/components/addressControls/AddEditAddressContactsControl';
import ManuallyAddEditAddressControl from '@/ui/components/addressControls/ManualAddEditAddressControl';
import { Button, Conditional, Modal, ModalProps } from '@payaca/ambrosia';

import { FC, useState } from 'react';

type Address = Pick<
  _Address,
  | 'id'
  | 'line1'
  | 'line2'
  | 'city'
  | 'country'
  | 'postalCode'
  | 'fullLocalAddress'
  | 'contacts'
>;

export interface IProps extends Omit<ModalProps, 'onBack' | 'title'> {
  title?: string;
  addresses: Address[];
  onChange?: (
    address: Omit<Address, 'contacts'> & {
      contacts: UpdateAddressContactInput[];
    }
  ) => void;
  onRemoveAddress?: (addressId: string) => void;
}

const EditMultipleAddressesAndAddressContactsModal: FC<IProps> = (props) => {
  const {
    title = 'Edit multiple Addresses',
    addresses,
    onChange,
    onRemoveAddress,
    ...rest
  } = props;

  const [isEditingMode, setIsEditingMode] = useState<string>();

  return (
    <Modal
      title={title}
      onBack={isEditingMode ? () => setIsEditingMode(undefined) : undefined}
      {...rest}
    >
      <Conditional
        condition={!isEditingMode}
        fallback={
          <ManuallyAddEditAddressControl
            isInModal
            address={addresses.find((address) => address.id === isEditingMode)}
            onSave={(updatedAddress) => {
              const currentAddress = addresses.find(
                (address) => address.id === isEditingMode
              );

              if (!currentAddress) {
                return;
              }

              onChange?.({
                ...currentAddress,
                ...updatedAddress,
              });

              setIsEditingMode(undefined);
            }}
            onClose={() => {
              setIsEditingMode(undefined);
            }}
          />
        }
      >
        <Modal.Body>
          <div className="flex flex-col gap-4">
            {addresses.map((address, index) => {
              return (
                <div
                  key={address.id}
                  className={
                    'space-y-4' + (index !== 0 ? ' border-t pt-4' : '')
                  }
                >
                  <div className="flex items-start justify-between gap-4">
                    <p>{address.fullLocalAddress}</p>

                    <Button
                      className="whitespace-nowrap"
                      variant={'link-inline'}
                      onClick={(e) => {
                        onRemoveAddress?.(address.id);
                      }}
                    >
                      Remove address
                    </Button>
                  </div>

                  <div>
                    <Button
                      variant={'link-inline'}
                      onClick={(e) => {
                        setIsEditingMode(address.id);
                      }}
                    >
                      Edit Address
                    </Button>
                  </div>

                  <AddEditAddressContactsControl
                    contacts={address.contacts || []}
                    onUpdate={(newContacts) => {
                      const copyAddress = { ...address };

                      onChange?.({
                        ...copyAddress,
                        contacts: newContacts,
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Modal.Body>
      </Conditional>
    </Modal>
  );
};

export default EditMultipleAddressesAndAddressContactsModal;
